import { v4 as uuidv4 } from "uuid";
import store from "@/store/store.js";
import PersonaHelpers from "@/helpers/PersonaHelpers.js";

export default class Helpers {
  /**
   * Freezes an object and all its nested objects to prevent Vue reactivity overhead
   * This significantly improves performance for large datasets and prevents crashes
   * @param {Object} obj - The object to freeze
   * @param {number} maxDepth - Maximum depth to freeze (default: 10)
   * @returns {Object} The frozen object
   */
  static deepFreeze(obj, maxDepth = 10, currentDepth = 0) {
    if (currentDepth >= maxDepth) {
      return obj;
    }

    if (obj && typeof obj === "object" && !Object.isFrozen(obj)) {
      // Freeze all properties
      Object.keys(obj).forEach((key) => {
        if (obj[key] && typeof obj[key] === "object") {
          obj[key] = this.deepFreeze(obj[key], maxDepth, currentDepth + 1);
        }
      });

      // Freeze the object itself
      return Object.freeze(obj);
    }

    return obj;
  }

  /**
   * Freezes tree items recursively to prevent Vue reactivity overhead
   * @param {Object} item - The tree item to freeze
   * @returns {Object} The frozen tree item
   */
  static freezeTreeItem(item) {
    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => this.freezeTreeItem(child));
    }
    return Object.freeze(item);
  }

  static generateUuid() {
    return uuidv4();
  }

  static parseJSON(str) {
    try {
      if (typeof str === "string") {
        return JSON.parse(str);
      } else if (typeof str === "object") {
        return str;
      } else {
        return str;
      }
    } catch (error) {
      console.debug("Could not parse str", error);
      return str;
    }
  }

  static lazyLoad(view, path = "views") {
    return () => import(`@/${path}/${view}.vue`);
  }

  static generateStringArrayWithFilterRules(feature) {
    // TODO: There shouldn't be string keyword
    if (typeof feature?.properties?.keywords === "string") {
      feature.properties.keywords = [feature.properties.keywords];
    }
    if (feature?.properties === undefined) {
      return [];
    }
    return [
      feature.properties.name,
      feature.properties.name?.toString().split(" "),
      feature.properties.fid,
      feature.properties.eid,
      feature.properties.description,
      feature.properties.description?.split(" "),
      feature.properties.typeCode,
      feature.properties.typeCode?.split("-"),
      feature.properties.typeCode?.replace("-", " "),
      feature.properties.keywords?.map((str) => String(str)).map((str) => str.split(" ")),
      feature.properties.uuid,
      feature.properties.major,
      feature.properties.minor,
      feature.properties.major + "X" + feature.properties.minor
    ]
      .filter((n) => n) // remove invalid fields
      .flat()
      .map(this.clearText);
  }

  static clearText(str) {
    if (typeof str != "string") {
      return str;
    }
    return str.trim().toLowerCase();
  }

  static escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  static arrayToObject(arr) {
    try {
      return arr.reduce((accum, [k, v]) => {
        accum[k] = v;
        return accum;
      }, {});
    } catch (err) {
      console.debug(`Failed to convert array(${arr}) to Object`, err);
      return arr;
    }
  }

  static sortByName(a, b) {
    if (a?.properties?.name?.toLowerCase() < b?.properties?.name?.toLowerCase()) {
      return -1;
    }
    if (a?.properties?.name?.toLowerCase() > b?.properties?.name?.toLowerCase()) {
      return 1;
    }
    return 0;
  }

  static getMapObjectTree() {
    const currentSiteData = store.state.CONTENT.currentSiteData;
    const mapObjects = store.getters["CONTENT/mapObjectsForSelectedPersona"]();
    const { buildings, levels } = currentSiteData;
    const currentClient = store.state.MAP.currentClient;
    const currentSite = store.state.MAP.currentSite;
    const items = [];
    buildings.forEach((building) => {
      const bid = building.properties.fid;
      const buildingLevels = levels.filter((level) => level.properties.bid === bid);
      let buildingItem = {
        id: bid,
        name: building.properties.name,
        icon: "foldersIcons",
        routeName: "BuildingEdit",
        params: {
          clientId: currentClient,
          siteId: currentSite,
          buildingId: bid
        }
      };
      if (building.properties.isTemp) {
        buildingItem.isTemp = true;
      } else if (buildingLevels.length > 0) {
        buildingItem.children = buildingLevels.map((level) => ({
          id: `${bid}-${level.properties.lvl}`,
          name: `${level.properties.lvl}\u00A0\u00A0\u00A0${level.properties.name} (${level.properties.shortName})`,
          levelIndex: level.properties.lvl,
          isDefault: !!level?.properties?.isDefault,
          children: []
        }));
      }
      if (buildingLevels.length === 0) {
        buildingItem.shouldDisplayNoLevels = true;
      }
      items.push(buildingItem);
    });

    mapObjects.forEach((mapObject) => {
      const bid = mapObject.properties.bid;
      const levelId = mapObject.properties.lvl;
      const typeCode = mapObject.properties.typeCode;

      // Skip georeferencing-anchor typeCodes
      if (typeCode === "georeferencing-anchor") {
        return;
      }

      const buildingItem = items?.find((item) => item.id === bid);
      const levelItem = buildingItem?.children?.find((item) => item.id === `${bid}-${levelId}`);
      if (!levelItem?.children?.find((item) => item.id === `${bid}-${levelId}-${typeCode}`)) {
        levelItem?.children?.push({
          id: `${bid}-${levelId}-${typeCode}`,
          name: typeCode,
          icon: typeCode,
          sprite: "sprite",
          bid: bid,
          lvl: levelId,
          children: []
        });
      }
      const currentClient = store.state.MAP.currentClient;
      const currentSite = store.state.MAP.currentSite;

      levelItem?.children
        ?.find((item) => item.id === `${bid}-${levelId}-${typeCode}`)
        ?.children.push({
          id: mapObject.properties.fid,
          name: mapObject.properties.name || mapObject.properties.fid,
          icon: "map-pin-blue",
          iconColor: "theme-darken1",
          isLeaf: true,
          params: {
            clientId: currentClient,
            siteId: currentSite,
            buildingId: mapObject.properties.bid,
            levelId: mapObject.properties.lvl,
            featureId: mapObject.properties.fid
          },
          routeName: "MapContentEdit",
          personas: PersonaHelpers.getPersonasForFeature(mapObject)
        });
    });

    // Sort typeCodes by name
    items.forEach((building) => {
      building?.children?.forEach((level) => {
        level.children?.sort((a, b) => a.name.localeCompare(b.name));
      });
    });

    // Add children counts (number of map objects) to parent nodes
    items.forEach((building) => {
      let buildingCount = 0;
      building.children?.forEach((level) => {
        let levelCount = 0;
        level.children?.forEach((typeCode) => {
          const typeCodeCount = typeCode.children?.length || 0;
          typeCode.childrenCount = typeCodeCount;
          levelCount += typeCodeCount;
        });
        level.childrenCount = levelCount;
        buildingCount += levelCount;
      });
      building.childrenCount = buildingCount;
    });

    // Freeze all tree items to prevent Vue reactivity overhead and improve performance
    items.forEach((item) => this.freezeTreeItem(item));

    return items;
  }
  static filterGpsGeofences(geofences, filterText) {
    if (!filterText) {
      return geofences;
    }
    const escapedFilterText = this.escapeRegExp(filterText);
    let filter = RegExp(`(${escapedFilterText})`, "i");
    return geofences.filter(
      (gpsGeofence) => filter.test(gpsGeofence?.properties?.name) || filter.test(gpsGeofence?.properties?.eid)
    );
  }
  static getGeofenceTree(filterText) {
    // Use filtered results from the store when filtering, otherwise use raw data
    const beaconGeofences =
      filterText && filterText.trim()
        ? store.state.CONTENT.filteredBeaconGeofences
        : store.getters["CONTENT/beaconGeofencesForSelectedPersona"]();
    const gpsGeofences = this.filterGpsGeofences(store.getters["CONTENT/gpsGeofencesForSelectedPersona"](), filterText);
    const currentSiteData = store.state.CONTENT.currentSiteData;
    const { buildings, levels } = currentSiteData;
    const currentClient = store.state.MAP.currentClient;
    const currentSite = store.state.MAP.currentSite;
    let items = [];

    if (gpsGeofences.length > 0) {
      items.push({
        id: "gps-geofences",
        name: "GPS Geofences",
        icon: "foldersIcons-2",
        children: [
          ...gpsGeofences.map((gpsGeofence) => ({
            id: gpsGeofence.properties.fid,
            name: gpsGeofence.properties.name,
            icon: "bezier-curve-02",
            isLeaf: true,
            params: {
              clientId: currentClient,
              siteId: currentSite,
              gpsGeofenceId: gpsGeofence.properties.fid
            },
            routeName: "GpsGeofenceEdit",
            personas: PersonaHelpers.getPersonasForFeature(gpsGeofence)
          }))
        ],
        childrenCount: gpsGeofences.length
      });
    }

    // Create building structure only if there are matching beacon geofences
    const buildingsWithGeofences = new Set();
    const levelsByBuilding = new Map();

    beaconGeofences.forEach((beaconGeofence) => {
      const bid = beaconGeofence.properties.bid;
      const levelId = beaconGeofence.properties.lvl;
      buildingsWithGeofences.add(bid);

      if (!levelsByBuilding.has(bid)) {
        levelsByBuilding.set(bid, new Set());
      }
      levelsByBuilding.get(bid).add(levelId);
    });

    buildings.forEach((building) => {
      const bid = building.properties.fid;

      // Only include buildings that have matching geofences when filtering
      if (filterText && filterText.trim() && !buildingsWithGeofences.has(bid)) {
        return;
      }

      const buildingLevels = levels.filter((level) => level.properties.bid === bid);
      let buildingItem = {
        id: bid,
        name: building.properties.name,
        icon: "foldersIcons",
        routeName: "BuildingEdit",
        params: {
          clientId: currentClient,
          siteId: currentSite,
          buildingId: bid
        }
      };

      if (building.properties.isTemp) {
        buildingItem.isTemp = true;
      } else if (buildingLevels.length > 0) {
        buildingItem.children = buildingLevels
          .filter((level) => {
            // Only include levels that have matching geofences when filtering
            return !filterText || !filterText.trim() || levelsByBuilding.get(bid)?.has(level.properties.lvl);
          })
          .map((level) => ({
            id: `${bid}-${level.properties.lvl}`,
            name: `${level.properties.lvl}\u00A0\u00A0\u00A0${level.properties.name} (${level.properties.shortName})`,
            levelIndex: level.properties.lvl,
            isDefault: !!level?.properties?.isDefault,
            children: []
          }));
      }

      if (buildingLevels.length === 0) {
        buildingItem.shouldDisplayNoLevels = true;
      }

      // Only add building if it has children when filtering
      if (
        !filterText ||
        !filterText.trim() ||
        (buildingItem.children && buildingItem.children.length > 0) ||
        buildingItem.shouldDisplayNoLevels
      ) {
        items.push(buildingItem);
      }
    });

    beaconGeofences.forEach((beaconGeofence) => {
      const bid = beaconGeofence.properties.bid;
      const levelId = beaconGeofence.properties.lvl;
      const buildingItem = items.find((item) => item.id === bid);

      const item = {
        id: beaconGeofence.properties.fid,
        name: beaconGeofence.properties.name,
        icon: "bezier-curve-02",
        isLeaf: true,
        params: {
          clientId: currentClient,
          siteId: currentSite,
          buildingId: beaconGeofence.properties.bid,
          levelId: beaconGeofence.properties.lvl,
          featureId: beaconGeofence.properties.fid
        },
        routeName: "BeaconGeofenceEdit",
        personas: PersonaHelpers.getPersonasForFeature(beaconGeofence)
      };

      if (buildingItem && buildingItem.children) {
        let levelItem = buildingItem.children.find((item) => item.id === `${bid}-${levelId}`);
        if (levelItem) {
          levelItem.children.push(item);
        }
      }
    });

    items.forEach((building) => {
      if (building.id === "gps-geofences") {
        return;
      }
      let buildingCount = 0;
      building.children?.forEach((level) => {
        let levelCount = level.children?.length || 0;
        level["childrenCount"] = levelCount;
        buildingCount += levelCount;
      });
      building["childrenCount"] = buildingCount;
    });

    // Freeze all tree items to prevent Vue reactivity overhead and improve performance
    items.forEach((item) => this.freezeTreeItem(item));

    return items;
  }

  static getBeaconTree() {
    const filterText = store.state.CONTENT.filterText;
    // Use filtered results from the store when filtering, otherwise use raw data
    const beacons = filterText && filterText.trim() ? store.state.CONTENT.filteredBeacons : store.state.CONTENT.beacons;
    const currentSiteData = store.state.CONTENT.currentSiteData;
    const { buildings, levels } = currentSiteData;
    const currentClient = store.state.MAP.currentClient;
    const currentSite = store.state.MAP.currentSite;
    let items = [];

    // Create building structure only if there are matching beacons
    const buildingsWithBeacons = new Set();
    const levelsByBuilding = new Map();

    beacons.forEach((beacon) => {
      const bid = beacon.properties.bid;
      const levelId = beacon.properties.lvl;
      buildingsWithBeacons.add(bid);

      if (!levelsByBuilding.has(bid)) {
        levelsByBuilding.set(bid, new Set());
      }
      levelsByBuilding.get(bid).add(levelId);
    });

    buildings.forEach((building) => {
      const bid = building.properties.fid;

      // Only include buildings that have matching beacons when filtering
      if (filterText && filterText.trim() && !buildingsWithBeacons.has(bid)) {
        return;
      }

      const buildingLevels = levels.filter((level) => level.properties.bid === bid);
      let buildingItem = {
        id: bid,
        name: building.properties.name,
        icon: "foldersIcons",
        routeName: "BuildingEdit",
        params: {
          clientId: currentClient,
          siteId: currentSite,
          buildingId: bid
        }
      };

      if (building.properties.isTemp) {
        buildingItem.isTemp = true;
      } else if (buildingLevels.length > 0) {
        buildingItem.children = buildingLevels
          .filter((level) => {
            // Only include levels that have matching beacons when filtering
            return !filterText || !filterText.trim() || levelsByBuilding.get(bid)?.has(level.properties.lvl);
          })
          .map((level) => ({
            id: `${bid}-${level.properties.lvl}`,
            name: `${level.properties.lvl}\u00A0\u00A0\u00A0${level.properties.name} (${level.properties.shortName})`,
            levelIndex: level.properties.lvl,
            isDefault: !!level?.properties?.isDefault,
            children: []
          }));
      }

      if (buildingLevels.length === 0) {
        buildingItem.shouldDisplayNoLevels = true;
      }

      // Only add building if it has children when filtering
      if (
        !filterText ||
        !filterText.trim() ||
        (buildingItem.children && buildingItem.children.length > 0) ||
        buildingItem.shouldDisplayNoLevels
      ) {
        items.push(buildingItem);
      }
    });

    beacons.forEach((beacon) => {
      const bid = beacon.properties.bid;
      const levelId = beacon.properties.lvl;
      const buildingItem = items.find((item) => item.id === bid);

      if (buildingItem && buildingItem.children) {
        const levelItem = buildingItem.children.find((item) => item.id === `${bid}-${levelId}`);

        if (levelItem) {
          const beaconItem = {
            id: beacon.properties.fid,
            name: `${beacon.properties.major}X${beacon.properties.minor}`,
            icon: "beacon",
            isLeaf: true,
            params: {
              clientId: currentClient,
              siteId: currentSite,
              buildingId: beacon.properties.bid,
              levelId: beacon.properties.lvl,
              featureId: beacon.properties.fid
            },
            routeName: "BeaconEdit"
          };
          levelItem.children.push(beaconItem);
        }
      }
    });

    items.forEach((building) => {
      let buildingCount = 0;
      building.children?.forEach((level) => {
        const levelCount = level.children?.length || 0;
        level.childrenCount = levelCount;
        buildingCount += levelCount;
      });
      building.childrenCount = buildingCount;
    });

    // Freeze all tree items to prevent Vue reactivity overhead and improve performance
    items.forEach((item) => this.freezeTreeItem(item));

    return items;
  }

  /**
   * Creates a handler function for exclusive tree expansion
   * @param {Ref} openItemsRef - Vue ref containing array of open item IDs
   * @param {Function} getTreeData - Function that returns the current tree data
   * @returns {Function} Handler function for @update:open event
   */
  static createTreeToggleHandler(openItemsRef, getTreeData) {
    return (openItemsArray) => {
      const getParentChildMap = (items, parentId = null, map = new Map()) => {
        items.forEach((item) => {
          map.set(item.id, parentId);
          if (item.children) {
            getParentChildMap(item.children, item.id, map);
          }
        });
        return map;
      };

      const parentMap = getParentChildMap(getTreeData());
      const newlyOpened = openItemsArray.filter((id) => !openItemsRef.value.includes(id));

      if (newlyOpened.length > 0) {
        // For each newly opened item, close only its siblings
        let itemsToKeepOpen = [...openItemsRef.value];

        newlyOpened.forEach((newId) => {
          const parentOfNew = parentMap.get(newId);

          // Find siblings (items with the same parent) and remove them from open items
          itemsToKeepOpen = itemsToKeepOpen.filter((openId) => {
            const parentOfOpen = parentMap.get(openId);
            // Keep if it's not a sibling (different parent) or if it's the newly opened item
            return parentOfOpen !== parentOfNew || openId === newId;
          });

          // Add the newly opened item
          if (!itemsToKeepOpen.includes(newId)) {
            itemsToKeepOpen.push(newId);
          }
        });

        openItemsRef.value = itemsToKeepOpen;
      } else {
        // Items are being closed, just update normally
        openItemsRef.value = [...openItemsArray];
      }
    };
  }

  /**
   * Creates tree click handling functionality for making whole tree items clickable
   * @param {Object} options - Configuration object
   * @param {Function} options.getTreeData - Function that returns current tree data
   * @param {Function} options.editCallback - Function to call when item is clicked
   * @param {boolean} options.requireRouteName - Whether to also require routeName property (default: false)
   * @param {string} options.treeSelector - CSS selector for the tree element (default: '.content-tree-list')
   * @returns {Object} Object with setup, cleanup, and utility functions
   */
  static createTreeClickHandler(options) {
    const { getTreeData, editCallback, requireRouteName = false, treeSelector = ".content-tree-list" } = options;

    let clickHandler = null;

    // Find clickable item in tree data
    const findClickableItem = (items, targetId) => {
      for (const item of items) {
        const hasParams = item.params;
        const hasRouteName = !requireRouteName || item.routeName;

        if (item.id === targetId && hasParams && hasRouteName) {
          return item;
        }
        if (item.children) {
          const found = findClickableItem(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    // Fallback function for finding items by name (for backward compatibility)
    const findClickableItemByName = (items, targetName) => {
      for (const item of items) {
        const hasParams = item.params;
        const hasRouteName = !requireRouteName || item.routeName;

        if (item.name === targetName && hasParams && hasRouteName) {
          return item;
        }
        if (item.children) {
          const found = findClickableItemByName(item.children, targetName);
          if (found) return found;
        }
      }
      return null;
    };

    // Extract item ID from tree node - looks for data-item-id attribute
    const extractItemId = (nodeRoot) => {
      const labelElement = nodeRoot.querySelector("[data-item-id]");
      if (labelElement) {
        return labelElement.dataset.itemId;
      }

      // Fallback to extracting from text content for views that haven't been updated yet
      const fallbackLabelElement = nodeRoot.querySelector(".v-treeview-node__label");
      if (!fallbackLabelElement) return null;

      // Try complex structure first (MapContentView style with flex containers)
      const flexContainer = fallbackLabelElement.querySelector(".d-flex");
      if (flexContainer) {
        const nameDiv = flexContainer.querySelector("div:first-child");
        if (nameDiv) {
          return nameDiv.textContent?.trim();
        }
      }

      // Fall back to simple structure (GeofencesView style)
      const nameDiv = fallbackLabelElement.querySelector("div");
      if (nameDiv) {
        return nameDiv.textContent?.trim();
      }

      return null;
    };

    // Main click handler
    const handleTreeClick = (event) => {
      const nodeRoot = event.target.closest(".v-treeview-node__root");
      if (!nodeRoot) return;

      // Don't handle clicks on expand/collapse buttons or edit buttons
      if (
        event.target.closest(".v-treeview-node__toggle") ||
        event.target.closest(".edit-button") ||
        event.target.closest("button")
      ) {
        return;
      }

      // Quick check: only process if this node has an edit button (is clickable)
      const hasEditButton = nodeRoot.querySelector(".edit-button");
      if (!hasEditButton) {
        return; // Let parent nodes expand/collapse normally
      }

      // Check if this is a leaf node (no expand icon visible = no children)
      const hasExpandIcon = nodeRoot.querySelector(".v-treeview-node__toggle");
      if (hasExpandIcon) {
        return; // Let nodes with children expand/collapse normally
      }

      const itemId = extractItemId(nodeRoot);
      if (!itemId) return;

      // Try to find by ID first (if data-item-id attribute exists)
      let item = null;
      const hasDataAttribute = nodeRoot.querySelector("[data-item-id]");
      if (hasDataAttribute) {
        item = findClickableItem(getTreeData(), itemId);
      } else {
        // Fallback to name-based search for views not yet updated
        item = findClickableItemByName(getTreeData(), itemId);
      }

      if (item) {
        store.commit("MAP/SHOULD_PREVENT_BUILDING_UPDATE_ON_MAP_MOVE", true);
        editCallback(item);
        setTimeout(() => {
          store.commit("MAP/SHOULD_PREVENT_BUILDING_UPDATE_ON_MAP_MOVE", false);
        }, 500);
      }
    };

    // Setup function to add event listeners
    const setup = (nextTick) => {
      if (nextTick) {
        nextTick(() => {
          const treeElement = document.querySelector(treeSelector);
          if (treeElement && !clickHandler) {
            clickHandler = handleTreeClick;
            treeElement.addEventListener("click", clickHandler);
          }
        });
      } else {
        const treeElement = document.querySelector(treeSelector);
        if (treeElement && !clickHandler) {
          clickHandler = handleTreeClick;
          treeElement.addEventListener("click", clickHandler);
        }
      }
    };

    // Cleanup function to remove event listeners
    const cleanup = () => {
      if (clickHandler) {
        const treeElement = document.querySelector(treeSelector);
        if (treeElement) {
          treeElement.removeEventListener("click", clickHandler);
        }
        clickHandler = null;
      }
    };

    return {
      setup,
      cleanup,
      findClickableItem,
      extractItemId
    };
  }

  /**
   * Creates a tree toggle handler with building/level synchronization
   * @param {Object} options - Configuration object
   * @param {Ref} options.openItemsRef - Vue ref containing array of open item IDs
   * @param {Function} options.getTreeData - Function that returns current tree data
   * @param {Object} options.store - Vuex store instance
   * @param {Computed} options.currentLevel - Current level computed ref
   * @param {Computed} options.currentBuildingObject - Current building object computed ref (optional)
   * @returns {Object} Object with handleItemToggle function and helper methods
   */
  static createTreeToggleHandlerWithSync(options) {
    const { openItemsRef, getTreeData, store, currentLevel, currentBuildingObject = null } = options;

    // Helper function to find an item in the tree by ID
    const findItemInTree = (items, targetId) => {
      for (const item of items) {
        if (item.id === targetId) {
          return item;
        }
        if (item.children) {
          const found = findItemInTree(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    // Helper function to find the parent building of an item
    const findParentBuilding = (items, targetId) => {
      for (const item of items) {
        if (item.routeName === "BuildingEdit" && item.children) {
          const isDirectChild = item.children.some((child) => child.id === targetId);
          if (isDirectChild) {
            return item;
          }
        }
      }
      return null;
    };

    // Fit map to building bounds with movement prevention
    const fitMapToBuilding = async () => {
      if (!currentBuildingObject) {
        return;
      }

      store.commit("MAP/SHOULD_PREVENT_BUILDING_UPDATE_ON_MAP_MOVE", true);
      const MapHelpers = await import("@/helpers/MapHelpers.js");
      MapHelpers.default.fitBounds(currentBuildingObject?.value?.geometry?.coordinates?.[0]);
      setTimeout(() => {
        store.commit("MAP/SHOULD_PREVENT_BUILDING_UPDATE_ON_MAP_MOVE", false);
      }, 1500);
    };

    // Ensure the current level is valid for the building, set to first if not
    const ensureValidLevelForBuilding = async (buildingItem) => {
      if (!buildingItem.children || buildingItem.children.length === 0) {
        return;
      }

      const currentLevelIndex = currentLevel.value;
      const levelExists = buildingItem.children.some((child) => child.levelIndex === currentLevelIndex);

      if (levelExists) {
        return;
      }

      const firstLevel = buildingItem.children[0];
      if (firstLevel?.levelIndex !== undefined) {
        await store.dispatch("MAP/SET_CURRENT_LEVEL", { level: firstLevel.levelIndex });
      }
    };

    // Handle building expansion logic
    const handleBuildingExpansion = async (item) => {
      const buildingId = item.params.buildingId;
      if (store.state.MAP.currentBuilding === buildingId) {
        return;
      }

      await store.dispatch("MAP/SET_CURRENT_BUILDING", { building: buildingId });
      await new Promise((resolve) => setTimeout(resolve, 100));
      await fitMapToBuilding();
      await ensureValidLevelForBuilding(item);
    };

    // Handle level expansion logic
    const handleLevelExpansion = async (item) => {
      const parentBuilding = findParentBuilding(getTreeData(), item.id);
      if (!parentBuilding || item.levelIndex === undefined) {
        return;
      }

      await store.dispatch("MAP/SET_CURRENT_LEVEL", { level: item.levelIndex });
    };

    // Handle item expansion - set building/level based on what was expanded
    const handleItemExpansion = async (item) => {
      if (item.routeName === "BuildingEdit" && item.params?.buildingId) {
        await handleBuildingExpansion(item);
        return;
      }

      await handleLevelExpansion(item);
    };

    // Flag to prevent infinite loops when programmatically setting open items
    let isUpdatingProgrammatically = false;

    // Custom toggle handler that sets current building/level on expansion
    const handleItemToggle = (openItemsArray) => {
      // If we're updating programmatically, don't process the change
      if (isUpdatingProgrammatically) {
        return;
      }

      // Process the toggle normally first
      const originalToggleHandler = Helpers.createTreeToggleHandler(openItemsRef, getTreeData);
      originalToggleHandler(openItemsArray);

      // Find newly opened items and handle expansions
      if (openItemsArray.length > 0) {
        openItemsArray.forEach((itemId) => {
          const item = findItemInTree(getTreeData(), itemId);
          if (item) {
            handleItemExpansion(item);
          }
        });
      }

      // Note: Removed the restriction that enforced current building/level to remain expanded
      // Users can now freely collapse any building or level, including the current one
    };

    // Helper function to expand tree for a specific building
    const expandTreeForBuilding = (buildingId) => {
      const buildingItem = getTreeData().find(
        (item) => item.routeName === "BuildingEdit" && item.params?.buildingId === buildingId
      );

      // Check if outdoor items are expanded - if so, don't auto-expand buildings
      const outdoorItemIds = ["outdoor-path-title", "gps-geofences"];
      const isOutdoorItemExpanded = outdoorItemIds.some((id) => openItemsRef.value.includes(id));

      if (buildingItem && !openItemsRef.value.includes(buildingItem.id) && !isOutdoorItemExpanded) {
        isUpdatingProgrammatically = true;
        openItemsRef.value = [buildingItem.id];
        // Reset flag after Vue's next tick to ensure the update is processed
        setTimeout(() => {
          isUpdatingProgrammatically = false;
        }, 0);
      }
    };

    // Helper function to expand tree for building and level
    const expandTreeForBuildingAndLevel = (buildingId, levelIndex, typeCode = null) => {
      const treeData = getTreeData();
      const buildingItem = treeData.find(
        (item) => item.routeName === "BuildingEdit" && item.params?.buildingId === buildingId
      );

      // Check if outdoor items are expanded - if so, don't auto-expand buildings
      const outdoorItemIds = ["outdoor-path-title", "gps-geofences"];
      const isOutdoorItemExpanded = outdoorItemIds.some((id) => openItemsRef.value.includes(id));

      if (buildingItem && !isOutdoorItemExpanded) {
        // Look for level using levelIndex property
        const levelItem = buildingItem.children?.find((child) => {
          return child.levelIndex === levelIndex;
        });

        let newOpenItems = [buildingItem.id];

        if (levelItem) {
          newOpenItems.push(levelItem.id);

          // If typeCode is provided, also expand the typeCode group
          if (typeCode) {
            const typeCodeItem = levelItem.children?.find((child) => {
              return child.id === typeCode;
            });
            if (typeCodeItem) {
              newOpenItems.push(typeCodeItem.id);
            }
          }
        }

        isUpdatingProgrammatically = true;
        openItemsRef.value = newOpenItems;
        // Reset flag after Vue's next tick to ensure the update is processed
        setTimeout(() => {
          isUpdatingProgrammatically = false;
        }, 0);
      }
    };

    // Helper function to expand tree for a specific map object (including its typeCode group)
    const expandTreeForMapObject = (buildingId, levelIndex, typeCode) => {
      if (!buildingId || levelIndex === undefined || !typeCode) {
        return;
      }

      // Use the enhanced expandTreeForBuildingAndLevel function with typeCode
      expandTreeForBuildingAndLevel(buildingId, levelIndex, typeCode);
    };

    return {
      handleItemToggle,
      expandTreeForBuilding,
      expandTreeForBuildingAndLevel,
      expandTreeForMapObject,
      findItemInTree,
      findParentBuilding
    };
  }

  /**
   * Creates an autoscroll helper for tree views
   * @param {string} treeSelector - CSS selector for the tree container (default: '.content-tree-list')
   * @returns {Function} Scroll function that takes an itemId and scrolls to the expanded element
   */
  static createTreeAutoScroll(treeSelector = ".content-tree-list") {
    return (itemId, nextTick) => {
      nextTick(() => {
        const treeContainer = document.querySelector(treeSelector);
        if (!treeContainer) return;

        const expandedNode = treeContainer.querySelector(`[data-item-id="${itemId}"]`)?.closest(".v-treeview-node");
        if (!expandedNode) return;

        const expandedRoot = expandedNode.querySelector(".v-treeview-node__root");
        if (!expandedRoot) return;

        const containerRect = treeContainer.getBoundingClientRect();
        const expandedRect = expandedRoot.getBoundingClientRect();

        const isVisible = expandedRect.top >= containerRect.top && expandedRect.bottom <= containerRect.bottom;

        if (!isVisible) {
          const scrollTop = treeContainer.scrollTop + (expandedRect.top - containerRect.top);
          treeContainer.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth"
          });
        }
      });
    };
  }

  /**
   * Common function to find any item in a tree by ID
   * @param {Array} items - Array of tree items
   * @param {string} targetId - ID to search for
   * @returns {Object|null} Found item or null
   */
  static findAnyItem(items, targetId) {
    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }
      if (item.children) {
        const found = Helpers.findAnyItem(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Common function to setup tree hover handlers
   * @param {string} treeSelector - CSS selector for the tree element
   * @param {Function} handleMouseEnter - Mouse enter handler function
   * @param {Function} handleMouseLeave - Mouse leave handler function
   * @param {Function} nextTick - Vue's nextTick function
   */
  static setupTreeHoverHandlers(treeSelector, handleMouseEnter, handleMouseLeave, nextTick) {
    nextTick(() => {
      const treeElement = document.querySelector(treeSelector);
      if (treeElement) {
        // Use mouseover/mouseout with delegation to only trigger on .v-treeview-node elements
        treeElement.addEventListener("mouseover", handleMouseEnter, true);
        treeElement.addEventListener("mouseout", handleMouseLeave, true);
      }
    });
  }

  /**
   * Common function to cleanup tree hover handlers
   * @param {string} treeSelector - CSS selector for the tree element
   * @param {Function} handleMouseEnter - Mouse enter handler function
   * @param {Function} handleMouseLeave - Mouse leave handler function
   */
  static cleanupTreeHoverHandlers(treeSelector, handleMouseEnter, handleMouseLeave) {
    const treeElement = document.querySelector(treeSelector);
    if (treeElement) {
      treeElement.removeEventListener("mouseover", handleMouseEnter, true);
      treeElement.removeEventListener("mouseout", handleMouseLeave, true);
    }
  }

  /**
   * Common function to handle tree mouse leave events
   * @param {Event} event - Mouse leave event
   * @param {Function} onItemMouseLeave - Function to call when leaving an item
   * @param {Function} findAnyItem - Function to find items in tree data
   * @param {Array} treeData - Current tree data
   * @returns {Object|null} Found item or null
   */
  static handleTreeMouseLeave(event, onItemMouseLeave, findAnyItem, treeData) {
    // Only handle events that originate from .v-treeview-node elements
    const treeNode = event.target.closest(".v-treeview-node");
    if (!treeNode) return null;

    // Check if we're actually leaving the tree node (not just moving to a child element)
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && treeNode.contains(relatedTarget)) {
      return null; // Still within the same tree node
    }

    const nodeRoot = treeNode.querySelector(".v-treeview-node__root");
    if (!nodeRoot) return null;

    const labelElement = nodeRoot.querySelector("[data-item-id]");
    if (!labelElement) return null;

    const itemId = labelElement.dataset.itemId;
    if (!itemId) return null;

    const item = findAnyItem(treeData, itemId);

    if (item) {
      onItemMouseLeave(item);
    }

    return item;
  }

  /**
   * Common function to handle tree mouse enter events
   * @param {Event} event - Mouse enter event
   * @param {Function} onItemMouseEnter - Function to call when entering an item
   * @param {Function} findAnyItem - Function to find items in tree data
   * @param {Array} treeData - Current tree data
   * @returns {Object|null} Found item or null
   */
  static handleTreeMouseEnter(event, onItemMouseEnter, findAnyItem, treeData) {
    // Only handle events that originate from .v-treeview-node elements
    const treeNode = event.target.closest(".v-treeview-node");
    if (!treeNode) return null;

    // Check if we're actually entering the tree node (not just moving from a child element)
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && treeNode.contains(relatedTarget)) {
      return null; // Still within the same tree node
    }

    const nodeRoot = treeNode.querySelector(".v-treeview-node__root");
    if (!nodeRoot) return null;

    const labelElement = nodeRoot.querySelector("[data-item-id]");
    if (!labelElement) return null;

    const itemId = labelElement.dataset.itemId;
    if (!itemId) return null;

    const item = findAnyItem(treeData, itemId);
    if (item) {
      onItemMouseEnter(item);
    }

    return item;
  }

  static getWayfindingTree() {
    const storeFilterText = store.state.CONTENT.filterText;
    // Use filtered results from the store when filtering, otherwise use raw data
    const graphs =
      storeFilterText && storeFilterText.trim()
        ? store.state.CONTENT.filteredGraphs
        : store.getters["CONTENT/graphsForSelectedPersona"]();
    const currentSiteData = store.state.CONTENT.currentSiteData;
    const { buildings, levels } = currentSiteData;
    const currentClient = store.state.MAP.currentClient;
    const currentSite = store.state.MAP.currentSite;
    let items = [];

    // Create a map of all features for quick lookup (used for both indoor and outdoor)
    const featureMap = new Map();
    graphs?.forEach((f) => {
      featureMap.set(f?.properties?.fid, f);
    });

    const outdoorPathNodes = graphs?.filter(
      (feature) =>
        feature.properties.typeCode === "path-node" &&
        feature.properties.lvl === undefined &&
        feature.properties.bid === undefined &&
        feature.properties.isOutdoor
    );
    const outdoorTransitionNodes = graphs?.filter(
      (feature) => feature.properties.typeCode === "custom-transition" && feature.properties.bid == undefined
    );
    const outdoorTransitionNodesThatHavePathGroupId = graphs?.filter((feature) => {
      return (
        feature?.properties?.typeCode !== "path-node" &&
        feature?.properties?.isOutdoor &&
        feature?.properties?.lvl === undefined &&
        feature?.properties?.bid === undefined &&
        feature?.properties?.pathGroupId
      );
    });

    if (outdoorPathNodes?.length > 0 || outdoorTransitionNodes.length > 0) {
      const outdoorNode = {
        id: "outdoor-path-title",
        name: "Outdoor Wayfinding Network",
        icon: "foldersIcons-2",
        children: []
      };

      // Group outdoor paths by pathGroupId
      // pathGroupId is always an array (even for single paths), so iterate directly
      const outdoorPathGroups = {};
      outdoorPathNodes.forEach((feature) => {
        const groupId = feature.properties.pathGroupId;
        if (!outdoorPathGroups[groupId]) {
          outdoorPathGroups[groupId] = [];
        }
        outdoorPathGroups[groupId].push(feature);
      });

      // Add outdoor transitions that have pathGroupId to the same groups
      // Transitions can belong to multiple paths, so add them to all groups they belong to
      // pathGroupId is always an array for transitions
      outdoorTransitionNodesThatHavePathGroupId.forEach((transition) => {
        const pathGroupIds = transition.properties.pathGroupId || [];
        if (Array.isArray(pathGroupIds)) {
          pathGroupIds.forEach((groupId) => {
            const groupKey = String(groupId);
            if (!outdoorPathGroups[groupKey]) {
              outdoorPathGroups[groupKey] = [];
            }
            // Only add if not already in the group
            if (!outdoorPathGroups[groupKey].find((f) => f.properties.fid === transition.properties.fid)) {
              outdoorPathGroups[groupKey].push(transition);
            }
          });
        } else {
          if (!outdoorPathGroups[pathGroupIds]) {
            outdoorPathGroups[pathGroupIds] = [];
          }
          outdoorPathGroups[pathGroupIds].push(transition);
        }
      });

      // Add outdoor paths group (similar to indoor paths structure)
      if (Object.keys(outdoorPathGroups).length > 0) {
        const outdoorPathsGroup = {
          id: `outdoor-paths-group`,
          name: "Paths",
          icon: "route",
          iconColor: "rgba(227, 228, 232, 0.5)",
          iconWidth: "16",
          iconHeight: "16",
          strokeColor: "foreground-base",
          isPathsGroup: true,
          colorClass: "paths-group",
          children: []
        };

        // Add individual outdoor paths to the Paths group
        Object.keys(outdoorPathGroups).forEach((groupId) => {
          const groupFeatures = outdoorPathGroups[groupId];
          const firstFeature = groupFeatures[0];
          const groupName = firstFeature.properties.name || "Outdoor Path";
          outdoorPathsGroup.children.push({
            id: `outdoor-path-${groupId}`,
            name: groupName,
            icon: "route",
            isPath: true,
            isOutdoor: true,
            pathGroupId: groupId,
            routeName: "OutdoorPathEdit",
            iconWidth: "16",
            iconHeight: "16",
            isLeaf: true,
            params: {
              clientId: currentClient,
              siteId: currentSite,
              groupId: groupId
            },
            personas: PersonaHelpers.getPersonasForFeature(firstFeature)
          });
        });

        // Sort outdoor paths alphabetically by name
        outdoorPathsGroup.children.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });

        outdoorNode.children.push(outdoorPathsGroup);
      }
      if (outdoorTransitionNodes.length > 0) {
        // group transition nodes by typeCode
        const transitionNodesByTypeCode = outdoorTransitionNodes.reduce((acc, node) => {
          acc[node.properties.typeCode] = acc[node.properties.typeCode] || [];
          acc[node.properties.typeCode].push(node);
          return acc;
        }, {});
        Object.keys(transitionNodesByTypeCode).forEach((typeCode) => {
          const typeCodeGroup = {
            id: `outdoor-transition-node-${typeCode}`,
            name: typeCode,
            icon: typeCode,
            iconWidth: "24",
            iconHeight: "24",
            iconColor: "rgba(227, 228, 232, 0.5)",
            strokeColor: "foreground-base",
            isTypeCodeGroup: true,
            typeCode: typeCode,
            children: transitionNodesByTypeCode[typeCode].map((node) => ({
              id: node.properties.fid,
              name: node.properties.name,
              icon: typeCode,
              iconWidth: "16",
              iconHeight: "16",
              strokeColor: "theme-darken1",
              iconColor: "rgba(227, 228, 232, 0.5)",
              routeName: "OutdoorTransitionEdit",
              isLeaf: true,
              params: {
                clientId: currentClient,
                siteId: currentSite,
                featureId: node.properties.fid
              },
              isTransitionNode: true,
              typeCode: typeCode,
              fid: node.properties.fid,
              personas: PersonaHelpers.getPersonasForFeature(node)
            }))
          };
          // Sort children alphabetically by name
          typeCodeGroup.children.sort((a, b) => {
            const nameA = (a.name || "").toLowerCase();
            const nameB = (b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
          outdoorNode.children.push(typeCodeGroup);
        });
      }
      items.push(outdoorNode);
    }

    // Create building structure only if there are matching graphs
    const buildingsWithGraphs = new Set();
    const levelsByBuilding = new Map();

    graphs?.forEach((graph) => {
      const bid = graph.properties.bid;
      const levelId = graph.properties.lvl;
      if (bid !== undefined && levelId !== undefined) {
        buildingsWithGraphs.add(bid);

        if (!levelsByBuilding.has(bid)) {
          levelsByBuilding.set(bid, new Set());
        }
        levelsByBuilding.get(bid).add(levelId);
      }
    });

    buildings.forEach((building) => {
      const bid = building.properties.fid;

      // Only include buildings that have matching graphs when filtering
      if (storeFilterText && storeFilterText.trim() && !buildingsWithGraphs.has(bid)) {
        return;
      }

      const buildingLevels = levels.filter((level) => level.properties.bid === bid);
      let buildingItem = {
        id: bid,
        name: building.properties.name,
        icon: "foldersIcons",
        routeName: "BuildingEdit",
        params: {
          clientId: currentClient,
          siteId: currentSite,
          buildingId: bid
        }
      };

      if (building.properties.isTemp) {
        buildingItem.isTemp = true;
      } else if (buildingLevels.length > 0) {
        buildingItem.children = buildingLevels
          .filter((level) => {
            // Only include levels that have matching graphs when filtering
            return !storeFilterText || !storeFilterText.trim() || levelsByBuilding.get(bid)?.has(level.properties.lvl);
          })
          .map((level) => ({
            id: `${bid}-${level.properties.lvl}`,
            name: `${level.properties.lvl}\u00A0\u00A0\u00A0${level.properties.name} (${level.properties.shortName})`,
            levelIndex: level.properties.lvl,
            isDefault: !!level?.properties?.isDefault,
            children: []
          }));
      }

      if (buildingLevels.length === 0) {
        buildingItem.shouldDisplayNoLevels = true;
      }

      // Only add building if it has children when filtering
      if (
        !storeFilterText ||
        !storeFilterText.trim() ||
        (buildingItem.children && buildingItem.children.length > 0) ||
        buildingItem.shouldDisplayNoLevels
      ) {
        items.push(buildingItem);
      }
    });

    const indoorPathNodes =
      graphs?.filter(
        (feature) =>
          feature.properties.typeCode === "path-node" &&
          feature.properties.bid !== undefined &&
          feature.properties.lvl !== undefined
      ) || [];
    const indoorTransitionNodesThatHavePathGroupId = graphs?.filter((feature) => {
      return (
        feature?.properties?.typeCode !== "path-node" &&
        feature?.properties?.lvl !== undefined &&
        feature?.properties?.bid !== undefined &&
        feature?.properties?.pathGroupId
      );
    });
    const indoorTransitionNodes =
      graphs?.filter(
        (feature) =>
          feature?.properties?.typeCode !== "path-node" &&
          feature?.properties?.bid !== undefined &&
          feature?.properties?.lvl !== undefined
      ) || [];

    // Group indoor path nodes by groupId
    // pathGroupId is always an array (even for single paths), so iterate directly
    const pathGroups = {};
    indoorPathNodes.forEach((feature) => {
      const groupId = feature.properties.pathGroupId;
      if (!pathGroups[groupId]) {
        pathGroups[groupId] = [];
      }
      pathGroups[groupId].push(feature);
    });

    // Add transitions that are neighbors of path-nodes to the same groups
    // Transitions can belong to multiple paths, so add them to all groups they belong to
    // pathGroupId is always an array for transitions
    indoorTransitionNodesThatHavePathGroupId.forEach((transition) => {
      const pathGroupIds = transition.properties.pathGroupId || [];
      if (Array.isArray(pathGroupIds)) {
        pathGroupIds.forEach((groupId) => {
          const groupKey = String(groupId);
          if (!pathGroups[groupKey]) {
            pathGroups[groupKey] = [];
          }
          // Only add if not already in the group
          if (!pathGroups[groupKey].find((f) => f.properties.fid === transition.properties.fid)) {
            pathGroups[groupKey].push(transition);
          }
        });
      } else {
        if (!pathGroups[pathGroupIds]) {
          pathGroups[pathGroupIds] = [];
        }
        pathGroups[pathGroupIds].push(transition);
      }
    });

    // Process each group separately
    Object.keys(pathGroups).forEach((groupId) => {
      const groupFeatures = pathGroups[groupId];
      const firstFeature = groupFeatures[0];
      const firstPathFeature = groupFeatures.find((feature) => feature.properties.typeCode === "path-node");
      const bid = firstFeature.properties.bid;
      const lvl = firstFeature.properties.lvl;
      // Use path-node name if available, otherwise use first feature name, otherwise default
      const groupName = firstPathFeature?.properties?.name || "Indoor Path";
      const buildingItem = items.find((item) => item.id === bid);

      if (buildingItem && buildingItem.children) {
        const levelItem = buildingItem.children.find((item) => item.id === `${bid}-${lvl}`);
        if (levelItem) {
          // Find or create Paths group
          let pathsGroup = levelItem.children.find((child) => child.id === `paths-group-${bid}-${lvl}`);
          if (!pathsGroup) {
            pathsGroup = {
              id: `paths-group-${bid}-${lvl}`,
              name: "Paths",
              icon: "route",
              iconColor: "rgba(227, 228, 232, 0.5)",
              iconWidth: "16",
              iconHeight: "16",
              strokeColor: "foreground-base",
              isPathsGroup: true,
              colorClass: "paths-group",
              children: []
            };
            levelItem.children.push(pathsGroup);
          }

          const pathId = `indoor-path-${bid}-${lvl}-${groupId}`;
          const hasIndoorPath = pathsGroup.children.find((child) => child.id === pathId);
          if (!hasIndoorPath) {
            pathsGroup.children.push({
              id: pathId,
              name: groupName,
              icon: "route",
              isPath: true,
              pathGroupId: groupId, // Single pathGroupId value (string) - tree item represents one path group
              isOutdoor: false,
              routeName: "PathEdit",
              isLeaf: true,
              params: {
                clientId: currentClient,
                siteId: currentSite,
                buildingId: bid,
                levelId: lvl,
                groupId: groupId
              },
              personas: PersonaHelpers.getPersonasForFeature(firstFeature)
            });
          }
        }
      }
    });

    indoorTransitionNodes.forEach((feature) => {
      const bid = feature.properties.bid;
      const lvl = feature.properties.lvl;
      const typeCode = feature.properties.typeCode;
      const buildingItem = items.find((item) => item.id === bid);

      if (buildingItem && buildingItem.children) {
        const levelItem = buildingItem.children.find((item) => item.id === `${bid}-${lvl}`);
        if (levelItem) {
          // Find or create typeCode group
          let typeCodeGroup = levelItem.children.find(
            (child) => child.id === `transition-group-${bid}-${lvl}-${typeCode}`
          );
          if (!typeCodeGroup) {
            typeCodeGroup = {
              id: `transition-group-${bid}-${lvl}-${typeCode}`,
              name: typeCode,
              icon: typeCode,
              iconColor: "rgba(227, 228, 232, 0.5)",
              iconWidth: "16",
              iconHeight: "16",
              strokeColor: "foreground-base",
              isTypeCodeGroup: true,
              typeCode: typeCode,
              children: []
            };
            levelItem.children.push(typeCodeGroup);
          }

          typeCodeGroup.children.push({
            id: feature.properties.fid,
            name: feature.properties.name || `${typeCode} ${feature.properties.fid}`,
            icon: typeCode,
            strokeColor: "theme-darken1",
            iconColor: "rgba(227, 228, 232, 0.5)",
            iconWidth: "16",
            iconHeight: "16",
            routeName: "TransitionEdit",
            isLeaf: true,
            params: {
              clientId: currentClient,
              siteId: currentSite,
              buildingId: bid,
              levelId: lvl,
              featureId: feature.properties.fid
            },
            isTransitionNode: true,
            typeCode: typeCode,
            fid: feature.properties.fid,
            personas: PersonaHelpers.getPersonasForFeature(feature)
          });
        }
      }
    });

    // Sort all typeCode group and paths group children alphabetically by name
    items.forEach((building) => {
      if (building.children) {
        building.children.forEach((level) => {
          if (level.children) {
            level.children.forEach((child) => {
              // Sort leaf nodes in typeCode groups
              if (child.isTypeCodeGroup && child.children) {
                child.children.sort((a, b) => {
                  const nameA = (a.name || "").toLowerCase();
                  const nameB = (b.name || "").toLowerCase();
                  return nameA.localeCompare(nameB);
                });
              }
              // Sort leaf nodes in paths groups
              if (child.isPathsGroup && child.children) {
                child.children.sort((a, b) => {
                  const nameA = (a.name || "").toLowerCase();
                  const nameB = (b.name || "").toLowerCase();
                  return nameA.localeCompare(nameB);
                });
              }
            });
          }
        });
      }
      // Also handle outdoor path title
      if (building.id === "outdoor-path-title" && building.children) {
        building.children.forEach((child) => {
          // Sort leaf nodes in typeCode groups
          if (child.isTypeCodeGroup && child.children) {
            child.children.sort((a, b) => {
              const nameA = (a.name || "").toLowerCase();
              const nameB = (b.name || "").toLowerCase();
              return nameA.localeCompare(nameB);
            });
          }
          // Sort leaf nodes in paths groups
          if (child.isPathsGroup && child.children) {
            child.children.sort((a, b) => {
              const nameA = (a.name || "").toLowerCase();
              const nameB = (b.name || "").toLowerCase();
              return nameA.localeCompare(nameB);
            });
          }
        });
      }
    });

    items.forEach((item) => {
      if (item.id === "outdoor-path-title") {
        let outdoorCount = 0;
        item.children?.forEach((child) => {
          if (child.id === "outdoor-paths-group") {
            // This is the paths group, count its children (outdoor paths)
            child.childrenCount = child.children?.length || 0;
            outdoorCount += child.children?.length || 0;
          } else if (child.children) {
            // This is a typeCode group, count its children (transition nodes)
            child.childrenCount = child.children?.length || 0;
            outdoorCount += child.children?.length || 0;
          } else {
            // This is a direct item
            outdoorCount += 1;
          }
        });
        item.childrenCount = outdoorCount;
        return;
      }

      let buildingCount = 0;
      item.children?.forEach((level) => {
        let levelCount = 0;
        level.children?.forEach((child) => {
          if (child.children) {
            // This is a typeCode group, count its children
            child.childrenCount = child.children?.length || 0;
            levelCount += child.children?.length || 0;
          } else {
            // This is a direct item (like indoor path)
            levelCount += 1;
          }
        });
        level.childrenCount = levelCount;
        buildingCount += levelCount;
      });
      item.childrenCount = buildingCount;
    });

    // Freeze all tree items to prevent Vue reactivity overhead and improve performance
    items.forEach((item) => this.freezeTreeItem(item));

    return items;
  }

  /**
   * Highlights a group of transition items by typeCode
   * @param {string} typeCode - The typeCode to highlight
   * @param {Array} transitions - Array of transition features
   */
  static highlightGroup(typeCode, transitions) {
    transitions.forEach((transition) => {
      this.highlightItem(typeCode, transition?.properties?.fid);
    });
  }

  /**
   * Unhighlights a group of transition items by typeCode
   * @param {string} typeCode - The typeCode to unhighlight
   * @param {Array} transitions - Array of transition features
   */
  static unhighlightGroup(typeCode, transitions) {
    transitions.forEach((transition) => {
      this.unhighlightItem(typeCode, transition?.properties?.fid);
    });
  }

  /**
   * Highlights a specific transition item
   * @param {string} typeCode - The typeCode of the transition
   * @param {string} fid - The feature ID of the transition
   */
  static highlightItem(typeCode, fid) {
    const className = `${typeCode.toLowerCase().replace(/[^a-zA-Z0-9]/g, "-")}-${fid}`;
    const element = document.getElementsByClassName(className)?.[0];
    if (element) {
      element.classList.add("highlighted");
    }
  }

  /**
   * Unhighlights a specific transition item
   * @param {string} typeCode - The typeCode of the transition
   * @param {string} fid - The feature ID of the transition
   */
  static unhighlightItem(typeCode, fid) {
    const className = `${typeCode.toLowerCase().replace(/[^a-zA-Z0-9]/g, "-")}-${fid}`;
    const element = document.getElementsByClassName(className)?.[0];
    if (element) {
      element.classList.remove("highlighted");
    }
  }

  /**
   * Checks if an item corresponds to the current building
   * @param {Object} item - Tree item to check
   * @param {string|number} currentBuilding - Current building ID
   * @returns {boolean} True if item is current building
   */
  static isCurrentBuilding(item, currentBuilding) {
    const result = item.routeName === "BuildingEdit" && item.params?.buildingId === currentBuilding;
    return result;
  }

  /**
   * Checks if an item corresponds to the current level
   * @param {Object} item - Tree item to check
   * @param {string|number} currentBuilding - Current building ID
   * @param {string|number} currentLevel - Current level ID
   * @returns {boolean} True if item is current level
   */
  static isCurrentLevel(item, currentBuilding, currentLevel) {
    if (item.levelIndex === undefined) {
      return false;
    }
    const result = item.levelIndex === currentLevel && item.id === `${currentBuilding}-${currentLevel}`;
    return result;
  }

  /**
   * Gets the appropriate CSS class for highlighting tree items
   * @param {Object} item - Tree item to check
   * @param {string|number} currentBuilding - Current building ID
   * @param {string|number} currentLevel - Current level ID
   * @param {boolean} isExpanded - Whether the item is expanded (optional, defaults to true for backward compatibility)
   * @returns {string} CSS class name for highlighting
   */
  static getItemHighlightClass(item, currentBuilding, currentLevel, isExpanded = true) {
    // Don't highlight collapsed items (minimized items should not be highlighted)
    if (isExpanded === false) {
      return "";
    }

    // Leaf items (items without children) are always considered "expanded" for highlighting purposes
    const isLeaf = !item.children || item.children.length === 0;
    if (isLeaf) {
      // Leaf items can always be highlighted
      if (Helpers.isCurrentBuilding(item, currentBuilding)) {
        return "current-building-highlight";
      }
      if (Helpers.isCurrentLevel(item, currentBuilding, currentLevel)) {
        return "current-level-highlight";
      }
      return "";
    }

    // For non-leaf items (buildings/levels), only highlight if expanded
    if (Helpers.isCurrentBuilding(item, currentBuilding)) {
      return "current-building-highlight";
    }
    if (Helpers.isCurrentLevel(item, currentBuilding, currentLevel)) {
      return "current-level-highlight";
    }
    return "";
  }

  /**
   * Applies highlighting classes to v-treeview nodes
   * @param {string} treeSelector - CSS selector for the tree container (e.g., '.map-content-tree', '.beacons-tree')
   * @param {Array} treeData - The tree data array
   * @param {string|number} currentBuilding - Current building ID
   * @param {string|number} currentLevel - Current level ID
   * @param {Function} nextTick - Vue's nextTick function
   * @param {Array} openItems - Array of open/expanded item IDs (optional, defaults to empty array)
   */
  static applyHighlightingToTreeNodes(treeSelector, treeData, currentBuilding, currentLevel, nextTick, openItems = []) {
    nextTick(() => {
      const treeNodes = document.querySelectorAll(`${treeSelector} .v-treeview-node`);

      const findItemInTree = (items, itemId) => {
        for (const item of items) {
          if (item.id === itemId) {
            return item;
          }
          if (item.children) {
            const found = findItemInTree(item.children, itemId);
            if (found) return found;
          }
        }
        return null;
      };

      treeNodes.forEach((node) => {
        const labelElement = node.querySelector(".v-treeview-node__label [data-item-id]");
        if (labelElement) {
          const itemId = labelElement.dataset.itemId;
          const item = findItemInTree(treeData, itemId);
          if (item) {
            // Check if item is expanded (for non-leaf items) or is a leaf item
            const isLeaf = !item.children || item.children.length === 0;
            const isExpanded = isLeaf || openItems.includes(itemId);
            const highlightClass = Helpers.getItemHighlightClass(item, currentBuilding, currentLevel, isExpanded);
            node.classList.remove("current-building-highlight", "current-level-highlight");
            if (highlightClass) {
              node.classList.add(highlightClass);
            }
          }
        }
      });
    });
  }

  /**
   * Finds all expanded typeCode items in a tree.
   * @param {Array} items - Tree items to search
   * @param {Array} openItems - Array of currently open item IDs
   * @returns {Array} Array of expanded typeCode item IDs
   */
  static findExpandedTypeCodeItems(items, openItems) {
    const expandedTypeCodeIds = [];
    const findTypeCodeItems = (treeItems) => {
      treeItems.forEach((item) => {
        // Check if item is expanded
        if (!openItems.includes(item.id)) {
          if (item.children) {
            findTypeCodeItems(item.children);
          }
          return;
        }

        // Map content tree: TypeCode items have icon and sprite properties but no routeName
        const isMapContentTypeCode = item.icon && item.sprite && !item.routeName;
        // Wayfinding tree: TypeCode items have isTypeCodeGroup or isPathsGroup properties
        const isWayfindingTypeCode = item.isTypeCodeGroup || item.isPathsGroup;

        if (isMapContentTypeCode || isWayfindingTypeCode) {
          expandedTypeCodeIds.push(item.id);
        }
        if (item.children) {
          findTypeCodeItems(item.children);
        }
      });
    };

    if (items && items.length > 0) {
      findTypeCodeItems(items);
    }

    return expandedTypeCodeIds;
  }

  /**
   * Checks if a typeCode item exists in the tree by its ID.
   * @param {Array} items - Tree items to search
   * @param {string} targetId - ID of the typeCode item to find
   * @returns {boolean} True if the typeCode item exists in the tree
   */
  static findTypeCodeItemInTree(items, targetId) {
    for (const item of items) {
      // Map content tree: TypeCode items have icon and sprite properties but no routeName
      const isMapContentTypeCode = item.id === targetId && item.icon && item.sprite && !item.routeName;
      // Wayfinding tree: TypeCode items have isTypeCodeGroup or isPathsGroup properties
      const isWayfindingTypeCode = item.id === targetId && (item.isTypeCodeGroup || item.isPathsGroup);

      if (isMapContentTypeCode || isWayfindingTypeCode) {
        return true;
      }
      if (item.children) {
        if (Helpers.findTypeCodeItemInTree(item.children, targetId)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Restores expanded typeCode items after a tree update.
   * @param {Array} expandedTypeCodeIds - Array of typeCode item IDs to restore
   * @param {Array} treeData - Updated tree data
   * @param {Ref|Array} openItems - Vue ref or array containing open item IDs
   */
  static restoreExpandedTypeCodeItems(expandedTypeCodeIds, treeData, openItems) {
    if (expandedTypeCodeIds.length === 0 || !treeData || treeData.length === 0) {
      return;
    }

    const openItemsArray = Array.isArray(openItems) ? openItems : openItems.value;
    const currentOpenItems = [...openItemsArray];

    expandedTypeCodeIds.forEach((typeCodeId) => {
      if (Helpers.findTypeCodeItemInTree(treeData, typeCodeId) && !currentOpenItems.includes(typeCodeId)) {
        currentOpenItems.push(typeCodeId);
      }
    });

    // Update the openItems
    if (Array.isArray(openItems)) {
      openItems.length = 0;
      openItems.push(...currentOpenItems);
    } else {
      openItems.value = currentOpenItems;
    }
  }

  /**
   * Recursively gets all leaf node IDs from a given item
   * Leaf nodes are items without children (mapcontent, transitions, geofences, paths, beacons, etc.)
   * @param {Object} item - The item to get the leaf items from (can be building, level, typeCode, etc.)
   * @returns {Array} Array of leaf item IDs
   */
  static getLeafItemsToSetPersonas(item) {
    if (!item) {
      return [];
    }

    // If item has no children, it's a leaf node
    if ((!item.children || item.children.length === 0) && item.isLeaf) {
      return [item.id];
    }
    if (!item.children) {
      return [];
    }
    // Recursively get leaf items from all children
    const leafItems = [];

    item.children.forEach((child) => {
      leafItems.push(...this.getLeafItemsToSetPersonas(child));
    });

    return leafItems;
  }
}
